import React, { FC } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useModal } from 'store/modal';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';
import { FaTrash } from 'react-icons/fa';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { usePermissionsContext } from 'store/permissions';
import { useTranslation } from 'react-i18next';

interface IProps {
  children?: any;
  onSuccess?: any;
  successMsg: string;
  confirmHeading: string;
  confirmMsg: any;
  id?: number;
  entity: string;
  queryKey?: any;
  onError?: any;
  hasPermission?: string;
  isDisabled?: boolean;
  disabledReason?: string;
}

const DeleteEntity: FC<IProps> = ({
  onSuccess,
  successMsg,
  confirmHeading,
  confirmMsg,
  id,
  entity,
  queryKey,
  onError,
  hasPermission,
  isDisabled,
  disabledReason = '',
}: IProps) => {
  const { t } = useTranslation();
  const { showModal } = useModal();
  const queryClient = useQueryClient();
  const { checkPermission } = usePermissionsContext();

  const isPermissionProvided = hasPermission ? checkPermission(hasPermission) : true;

  const options = {
    onSuccess: (res: any) => {
      showModal({
        type: 'success',
        content: successMsg,
      });
      if (queryKey) queryClient.invalidateQueries(queryKey);
      if (onSuccess) onSuccess(res);
    },
    onError: (res: any) => {
      showModal({
        type: 'error',
        content: createErrorFromResponse(res).message,
      });
      onError(res);
    },
  };

  const { mutate } = useMutation(entityId => API.delete(`${entity}/${entityId}`), options);

  const confirmDelete = (entityId: any) => {
    showModal({
      type: 'confirm',
      heading: confirmHeading,
      content: confirmMsg,
      onConfirm: () => mutate(entityId),
      confirmLabel: t('common.delete'),
      confirmBtnType: 'danger',
    });
  };

  const buildOverlayText = () => {
    return isDisabled ? disabledReason : t('common.delete');
  };

  return (
    <TooltipWrapper
      id="tooltip-trash"
      overlay={isPermissionProvided ? buildOverlayText() : t('common.noPermission')}
    >
      <Button
        btnType="danger"
        size="sm"
        outlined
        disabled={!isPermissionProvided || isDisabled}
        onClick={() => confirmDelete(id)}
      >
        <FaTrash size={14} />
      </Button>
    </TooltipWrapper>
  );
};

export default DeleteEntity;
